import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { translations } from "@/i18n";
import { Phone, Menu, X, Globe, Instagram } from "lucide-react";
import HeroPhone from "@/components/HeroPhone";
import HowItWorks from "@/components/sections/HowItWorks";
import WhyLove from "@/components/sections/WhyLove";
import Packages from "@/components/sections/Packages";
import Gallery from "@/components/sections/Gallery";
import VideoShowcase from "@/components/sections/VideoShowcase";
import FAQ from "@/components/sections/FAQ";
import RentalTerms from "@/components/sections/RentalTerms";
import BookingFlowV2 from "@/components/sections/BookingFlowV2";
import AboutTeam from "@/components/sections/AboutTeam";
import {
  ProductShowcase,
  PhoneCards,
  Extras,
  ClosingCTA,
} from "@/components/sections/StorySections";
import { trapTabKey } from "@/lib/focusTrap";

const INSTAGRAM_URL = "https://www.instagram.com/hellotelefonka/";
const INSTAGRAM_LABEL = "Telefonka Instagram";

export default function Landing({ lang, setLang }) {
  const t = translations[lang];
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const menuPanelRef = useRef(null);

  const closeMenu = () => setMenuOpen(false);
  const toggleLang = () => {
    closeMenu();
    setLang(lang === "en" ? "hu" : "en");
  };

  useEffect(() => {
    if (!menuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const menuButton = menuButtonRef.current;
    const focusFrame = window.requestAnimationFrame(() => {
      const panel = menuPanelRef.current;
      panel?.querySelector("a[href], button:not([disabled])")?.focus?.();
      if (panel && !panel.contains(document.activeElement)) panel.focus();
    });
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeMenu();
        return;
      }
      trapTabKey(event, menuPanelRef.current);
    };

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      window.removeEventListener("keydown", handleKeyDown);
      window.requestAnimationFrame(() => menuButton?.focus?.());
    };
  }, [menuOpen]);

  const navItems = [
    { id: "how", label: t.nav.how },
    { id: "love", label: t.nav.love },
    { id: "phones", label: t.nav.phones },
    { id: "packages", label: t.nav.packages },
    { id: "gallery", label: t.nav.gallery },
    { id: "team", label: t.nav.team },
    { id: "faq", label: t.nav.faq },
  ];

  const paymentTermsLabel =
    lang === "hu"
      ? "Bérleti, fizetési és lemondási feltételek"
      : "Rental, payment and cancellation terms";
  const bookingTrustCopy =
    lang === "hu"
      ? "A foglalási igény elküldése még nem jár fizetési kötelezettséggel."
      : "Sending your booking request does not require immediate payment.";
  const mainNavigationLabel = lang === "hu" ? "Fő navigáció" : "Main navigation";
  const openMenuLabel = lang === "hu" ? "Menü megnyitása" : "Open menu";
  const closeMenuLabel = lang === "hu" ? "Menü bezárása" : "Close menu";
  const languageLabel = lang === "hu" ? "Váltás angol nyelvre" : "Switch to Hungarian";
  const skipLabel = lang === "hu" ? "Ugrás a fő tartalomra" : "Skip to main content";

  return (
    <div data-testid="landing-root" className="relative overflow-x-hidden">
      <a href="#main-content" className="tk-skip-link">{skipLabel}</a>
      <header className="fixed top-0 left-0 right-0 z-40 backdrop-blur-md bg-[rgba(250,246,239,0.88)] border-b border-[rgba(110,30,43,0.08)]">
        <nav
          className="max-w-7xl mx-auto px-5 md:px-10 h-16 flex items-center justify-between"
          aria-label={mainNavigationLabel}
        >
          <a href="#top" data-testid="nav-logo" className="flex items-center gap-2 group" onClick={closeMenu}>
            <span className="w-9 h-9 rounded-full bg-[var(--burgundy)] text-white flex items-center justify-center shadow-md">
              <Phone size={16} aria-hidden="true" />
            </span>
            <span className="font-serif text-2xl tracking-tight text-[var(--ink)]">Telefonka</span>
          </a>

          <ul className="hidden xl:flex items-center gap-6">
            {navItems.map((item) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  data-testid={`nav-link-${item.id}`}
                  className="text-sm tk-nav-link"
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-2 sm:gap-3">
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-instagram"
              aria-label={INSTAGRAM_LABEL}
              title="Instagram"
              className="hidden sm:inline-flex w-11 h-11 items-center justify-center rounded-full border border-[rgba(44,31,26,0.18)] text-[var(--ink)] hover:border-[var(--burgundy)] hover:text-[var(--burgundy)] transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--burgundy)]"
            >
              <Instagram size={16} aria-hidden="true" />
            </a>
            <button
              type="button"
              onClick={toggleLang}
              data-testid="lang-toggle"
              className="min-h-11 text-xs uppercase tracking-widest flex items-center gap-1.5 px-3 rounded-full border border-[rgba(44,31,26,0.18)] hover:border-[var(--burgundy)] hover:text-[var(--burgundy)] transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--burgundy)]"
              aria-label={languageLabel}
            >
              <Globe size={13} aria-hidden="true" />
              {lang === "en" ? "EN" : "HU"}
            </button>
            <a
              href="#booking"
              data-testid="nav-cta"
              className="hidden md:inline-flex tk-btn-primary text-sm !py-2 !px-4 min-h-11"
            >
              {t.nav.cta}
            </a>
            <button
              ref={menuButtonRef}
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              data-testid="mobile-menu-toggle"
              className="xl:hidden w-11 h-11 inline-flex items-center justify-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--burgundy)]"
              aria-label={menuOpen ? closeMenuLabel : openMenuLabel}
              aria-expanded={menuOpen}
              aria-controls="mobile-navigation"
              aria-haspopup="dialog"
            >
              {menuOpen ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
            </button>
          </div>
        </nav>

        {menuOpen && (
          <div
            id="mobile-navigation"
            className="xl:hidden fixed inset-x-0 top-16 bottom-0 bg-[rgba(44,31,26,0.20)] backdrop-blur-[1px]"
            onClick={closeMenu}
          >
            <div
              ref={menuPanelRef}
              tabIndex={-1}
              role="dialog"
              aria-modal="true"
              aria-label={mainNavigationLabel}
              className="tk-dialog-focus-target max-h-[calc(100vh-4rem)] max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain bg-[var(--beige-50)] border-t border-[rgba(110,30,43,0.08)] px-5 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-lg"
              onClick={(event) => event.stopPropagation()}
            >
              <ul className="flex flex-col gap-1">
                {navItems.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      onClick={closeMenu}
                      data-testid={`mobile-nav-link-${item.id}`}
                      className="flex min-h-11 items-center py-2.5 text-[var(--ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--burgundy)]"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
                <li>
                  <a
                    href="#booking"
                    onClick={closeMenu}
                    className="tk-btn-primary justify-center w-full mt-3"
                  >
                    {t.nav.cta}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        )}
      </header>

      <main id="main-content" tabIndex={-1}>
        <section id="top" className="relative pt-32 md:pt-36 pb-16 md:pb-24 tk-section-anchor">
          <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div data-testid="hero-content" className="tk-fade-in">
              <span className="tk-eyebrow">
                <span className="tk-divider" />
                {t.hero.eyebrow}
              </span>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] mt-5 text-[var(--ink)] max-w-3xl">
                {t.hero.title} <span aria-hidden="true">–</span>
                <span className="block italic text-[var(--burgundy)] mt-1">
                  {t.hero.titleAccent}
                </span>
              </h1>
              <p className="mt-6 text-lg md:text-xl text-[var(--ink-soft)] max-w-xl">
                {t.hero.subtitle}
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <a href="#booking" data-testid="hero-cta-primary" className="tk-btn-primary">
                  {t.hero.primary}
                </a>
                <a href="#how" data-testid="hero-cta-secondary" className="tk-btn-secondary">
                  {t.hero.secondary}
                </a>
              </div>
              <p className="mt-4 max-w-xl text-xs sm:text-sm text-[var(--ink-soft)]/80 leading-relaxed">
                {bookingTrustCopy}
              </p>
              <div className="mt-8 flex flex-wrap gap-x-7 gap-y-3 text-sm text-[var(--ink-soft)]">
                {[t.hero.tag1, t.hero.tag2, t.hero.tag3].map((tag) => (
                  <span key={tag} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--burgundy)]" aria-hidden="true" />
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <HeroPhone t={t} />
          </div>
        </section>

        <HowItWorks t={t} />
        <WhyLove t={t} />
        <ProductShowcase t={t} />
        <PhoneCards t={t} />
        <Extras t={t} />
        <Packages t={t} />
        <Gallery t={t} />
        <VideoShowcase lang={lang} />
        <AboutTeam t={t} />
        <FAQ t={t} />
        <RentalTerms lang={lang} />
        <ClosingCTA t={t} />
        <BookingFlowV2 lang={lang} />
      </main>

      <footer className="bg-[var(--ink)] text-[var(--beige-100)] py-12">
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex flex-col md:flex-row items-center justify-between gap-5">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-full bg-[var(--burgundy)] text-white flex items-center justify-center">
              <Phone size={15} aria-hidden="true" />
            </span>
            <div>
              <p className="font-serif text-xl">Telefonka</p>
              <p className="text-xs opacity-70">{t.footer.tagline}</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <a
              href="#payment-terms"
              className="inline-flex min-h-11 items-center text-sm text-[var(--beige-100)]/85 hover:text-white transition px-4 rounded-full border border-white/15 hover:border-white/40"
            >
              {paymentTermsLabel}
            </a>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              aria-label={INSTAGRAM_LABEL}
              className="inline-flex min-h-11 items-center gap-2 text-sm text-[var(--beige-100)]/85 hover:text-white transition px-4 rounded-full border border-white/15 hover:border-white/40"
            >
              <Instagram size={14} aria-hidden="true" />
              <span>Instagram</span>
            </a>
            <p className="text-xs opacity-60">
              © {new Date().getFullYear()} Telefonka — {t.footer.rights}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
